import { Col, Row, Select, Skeleton, Typography } from "antd";
import { Uniform } from "components/Uniforms/Uniform";
import { PageTypes } from "enums/pageTypes.ts";
import { useUpdateFixtureEntityUniform } from "hooks/useFixturesApi";
import { useFetchUniforms } from "hooks/useUniformApi";
import { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { usePageType } from "../../../hooks/usePageType.ts";
import { useSportConfiguration } from "../../../hooks/useSportConfiguration.ts";

type Props = {
  fixture: any;
  entityId: any;
  viewOnly: boolean;
};

export const UniformTab = ({ fixture, entityId, viewOnly }: Props) => {
  const { formatMessage } = useIntl();
  const { data: uniformsResponse, isLoading: isUniformsLoading } = useFetchUniforms({ entityId });
  const { mutate: updateFixtureEntityUniform, isPending: isUpdateInProgress } = useUpdateFixtureEntityUniform();
  const [selectedUniform, setSelectedUniform] = useState<any>(null);
  const { uiConfiguration } = useSportConfiguration();
  const disabled = !fixture?.areRosterAndStaffEditable || false;
  const fixtureEntity = useMemo(() => {
    const fixtureEntities = [fixture?.awayEntity, fixture?.homeEntity].filter((it) => !!it);
    return fixtureEntities.find((it) => it.entityId === entityId);
  }, [fixture, entityId]);
  const pageType = usePageType();
  const uniformsOptions = (uniformsResponse?.data || []).map((x) => {
    return {
      value: x.uniformId,
      label: x.nameLocal || x.nameLatin,
      uniform: x,
    };
  });

  useEffect(() => {
    setSelectedUniform((uniformsResponse?.data || []).find((x: any) => x.uniformId === fixtureEntity?.uniformId));
  }, [fixtureEntity, uniformsResponse]);

  const onSelectChange = (value, option) => {
    updateFixtureEntityUniform({
      entityId,
      seasonId: fixture.seasonId,
      fixtureId: fixture.fixtureId,
      uniformId: value,
    });
    setSelectedUniform(option?.uniform);
  };

  if (isUniformsLoading) {
    return (
      <Row justify="center">
        <Col span={12}>
          <Skeleton />
        </Col>
      </Row>
    );
  }

  return (
    <Row justify="center">
      <Col span={12}>
        {pageType === PageTypes.TeamManager && uiConfiguration.uniforms.allowFixtureUniformChange && (
          <Row justify="center" className="mb-15">
            <Select
              allowClear
              style={{ width: "250px" }}
              showSearch
              disabled={viewOnly || disabled || isUpdateInProgress}
              placeholder={formatMessage({
                id: "select.uniform.placeholder",
              })}
              value={selectedUniform?.uniformId}
              options={uniformsOptions}
              onChange={onSelectChange}
            />
          </Row>
        )}
        <Row justify="center">
          {selectedUniform ? (
            <Uniform key={selectedUniform.uniformId} entityId={entityId} uniform={selectedUniform} readonly={true} />
          ) : (
            <Typography.Text type="secondary">
              {formatMessage({ id: "fixtureRosterPage.noUniformSelected" })}
            </Typography.Text>
          )}
        </Row>
      </Col>
    </Row>
  );
};
